
import React, { useState } from 'react';
import { Building, GripVertical, Home, X } from 'lucide-react';
import { Torre } from './types';

interface TorresTabsProps {
    torres: Torre[];
    unidadesSemTorreCount: number;
    activeTab: number;
    setActiveTab: (index: number) => void;
    removeTorre: (index: number) => void;
    moveTorre: (from: number, to: number) => void;
    renameTorre: (index: number, novoNome: string) => void;
}

const TorresTabs: React.FC<TorresTabsProps> = ({
    torres,
    unidadesSemTorreCount,
    activeTab,
    setActiveTab,
    removeTorre,
    moveTorre,
    renameTorre,
}) => {
    // Índice da torre sendo arrastada e da aba sobre a qual ela está no momento,
    // só para o feedback visual — a reordenação de fato acontece no drop.
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [overIndex, setOverIndex] = useState<number | null>(null);
    const [editandoTorreIndice, setEditandoTorreIndice] = useState<number | null>(null);
    const [novoNomeTorre, setNovoNomeTorre] = useState<string>("");

    const handleDrop = (destino: number) => {
        if (dragIndex !== null && dragIndex !== destino) moveTorre(dragIndex, destino);
        setDragIndex(null);
        setOverIndex(null);
    };

    const handleConfirmarRenomeio = (indice: number) => {
        if (novoNomeTorre.trim()) {
            renameTorre(indice, novoNomeTorre.trim());
        }
        setEditandoTorreIndice(null);
        setNovoNomeTorre("");
    };

    const handleKeyDownRenomeio = (e: React.KeyboardEvent, indice: number) => {
        if (e.key === "Enter") {
            handleConfirmarRenomeio(indice);
        } else if (e.key === "Escape") {
            setEditandoTorreIndice(null);
            setNovoNomeTorre("");
        }
    };

    return (
        <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-6 overflow-x-auto" aria-label="Tabs">
                {torres.map((torre, index) => {
                    const isDragging = dragIndex === index;
                    const isOver = overIndex === index && dragIndex !== index;
                    return (
                        <div
                            key={index}
                            draggable
                            onDragStart={(e) => {
                                setDragIndex(index);
                                e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                setOverIndex(index);
                            }}
                            onDragLeave={() => setOverIndex((atual) => (atual === index ? null : atual))}
                            onDrop={(e) => {
                                e.preventDefault();
                                handleDrop(index);
                            }}
                            onDragEnd={() => {
                                setDragIndex(null);
                                setOverIndex(null);
                            }}
                            title="Arraste para reordenar as torres"
                            className={`group relative flex-shrink-0 whitespace-nowrap inline-flex items-center py-4 px-1 border-b-2 cursor-grab active:cursor-grabbing transition-opacity ${
                                isDragging ? 'opacity-40' : ''
                            } ${isOver ? 'bg-indigo-50 ring-2 ring-indigo-300 rounded-t-md' : ''} ${
                                activeTab === index
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <GripVertical size={14} className="mr-1 text-gray-400" />
                            {editandoTorreIndice === index ? (
                                <input
                                    autoFocus
                                    type="text"
                                    value={novoNomeTorre}
                                    onChange={(e) => setNovoNomeTorre(e.target.value)}
                                    onBlur={() => handleConfirmarRenomeio(index)}
                                    onKeyDown={(e) => handleKeyDownRenomeio(e, index)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="ml-2 px-2 py-1 border border-indigo-500 rounded text-sm font-medium text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                    placeholder="Nome da torre"
                                />
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setActiveTab(index);
                                        setEditandoTorreIndice(index);
                                        setNovoNomeTorre(torre.nome);
                                    }}
                                    className="flex items-center hover:text-indigo-700 transition-colors"
                                    title="Clique para editar o nome da torre"
                                >
                                    <Building size={16} className="mr-2" />
                                    <span>{torre.nome}</span>
                                </button>
                            )}
                            {torres.length > 0 && (
                                <button type="button" onClick={() => removeTorre(index)} className="ml-2 text-gray-400 hover:text-red-500 opacity-50 group-hover:opacity-100 transition-opacity">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    );
                })}
                <button type="button" onClick={() => setActiveTab(torres.length)} className={`flex-shrink-0 whitespace-nowrap inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === torres.length ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                    <Home size={16} className="mr-2" />
                    Outras Unidades ({unidadesSemTorreCount})
                </button>
            </nav>
        </div>
    );
};

export default TorresTabs;
